// use-action-hook.ts — base helper for every action hook (mutations + util side-effects).
//
// Wraps React Query's useMutation to produce an ActionResult<V, T> per the protocol.
// See apps/fe/src/hooks/planning/async-protocol.md for the full contract.
//
// Behavior:
//  - Status transitions: NotInvoked -> Loading -> { Success | Errored }. reset() returns to NotInvoked.
//  - Handler bag is per-invocation, stored in a ref keyed by attempt id. Callers do NOT need
//    to memoize handlers — the latest handlers passed to invoke() fire for that attempt only.
//  - Dedupe (when invoke is called while Loading):
//      kind === Mutation (default) -> NoOp: ignore the new invoke; in-flight call settles normally.
//      kind === Query              -> CancelAndRestart: abort the in-flight call (if abortable),
//                                     drop its handlers (so the network resolution is a no-op),
//                                     start a new attempt with the new handlers.
//      Override per-hook via input.dedupeMode.
//  - Optimistic updates: opt-in via input.optimistic = { apply, rollback }. apply() runs
//    synchronously inside invoke; rollback() runs with the snapshot if the mutation rejects.
//  - Unmount safety: pending handlers are cleared on unmount and never fire. In-flight requests
//    are aborted when input.abortable is true.
//  - No global UI side effects. Errors surface only via onError. Toasts are the caller's concern.

'use client';

import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type MutableRefObject,
} from 'react';
import {
  ACTION_KIND,
  DEDUPE_MODE,
  INVOCATION_STATUS,
  type ActionKind,
  type ActionResult,
  type DedupeMode,
  type InvocationHandlers,
  type InvocationStatus,
} from '@/types/async-protocol';

export interface OptimisticUpdateOptions<TVariables, TSnapshot> {
  apply: (variables: TVariables) => TSnapshot;
  rollback: (snapshot: TSnapshot, variables: TVariables) => void;
}

export interface ActionHookOptions<TVariables, TData, TError = unknown, TSnapshot = unknown> {
  mutationFn: (variables: TVariables, ctx?: { signal: AbortSignal }) => Promise<TData>;
  // Called inside the helper's onSuccess, before the user's onSuccess fires. Use to invalidate
  // suite-owned cache keys via queryClient.invalidateQueries(...).
  onInvalidate?: (data: TData, variables: TVariables) => void;
  // Whether the helper should manage an AbortController and pass its signal to mutationFn.
  // Defaults to false. If true, dedupe (CancelAndRestart) and unmount will abort in-flight calls.
  abortable?: boolean;
  // Defaults to ACTION_KIND.Mutation. Read-shaped util hooks (e.g. useSignedUrlFetch) pass
  // ACTION_KIND.Query so the dedupe default flips to CancelAndRestart.
  kind?: ActionKind;
  // Override the kind-based default. Explicit value always wins.
  dedupeMode?: DedupeMode;
  optimistic?: OptimisticUpdateOptions<TVariables, TSnapshot>;
}

export function defaultDedupeModeForKind(kind: ActionKind): DedupeMode {
  return kind === ACTION_KIND.Query ? DEDUPE_MODE.CancelAndRestart : DEDUPE_MODE.NoOp;
}

interface MutateArgs<TVariables> {
  vars: TVariables;
  attemptId: number;
}

type ActionInputRef<
  TVariables,
  TData,
  TError,
  TSnapshot,
> = MutableRefObject<ActionHookOptions<TVariables, TData, TError, TSnapshot>>;

type ActionMutation<TVariables, TData, TError> = UseMutationResult<
  TData,
  TError,
  MutateArgs<TVariables>,
  unknown
>;

type ActionMutationRef<TVariables, TData, TError> = MutableRefObject<
  ActionMutation<TVariables, TData, TError>
>;

interface ActionRuntimeRefs<TData, TError, TSnapshot> {
  attemptIdRef: MutableRefObject<number>;
  handlersRef: MutableRefObject<Map<number, InvocationHandlers<TData, TError>>>;
  snapshotsRef: MutableRefObject<Map<number, TSnapshot>>;
  abortersRef: MutableRefObject<Map<number, AbortController>>;
  mountedRef: MutableRefObject<boolean>;
}

interface CleanupAttemptInput<TData, TError, TSnapshot> {
  refs: ActionRuntimeRefs<TData, TError, TSnapshot>;
  attemptId: number;
}

interface ActionMutationInput<TVariables, TData, TError, TSnapshot> {
  inputRef: ActionInputRef<TVariables, TData, TError, TSnapshot>;
  refs: ActionRuntimeRefs<TData, TError, TSnapshot>;
  cleanupAttempt: (attemptId: number) => void;
}

interface SuccessSettlementInput<TVariables, TData, TError, TSnapshot> {
  data: TData;
  vars: TVariables;
  attemptId: number;
  inputRef: ActionInputRef<TVariables, TData, TError, TSnapshot>;
  refs: ActionRuntimeRefs<TData, TError, TSnapshot>;
  cleanupAttempt: (attemptId: number) => void;
}

interface ErrorSettlementInput<TVariables, TData, TError, TSnapshot> {
  error: TError;
  vars: TVariables;
  attemptId: number;
  inputRef: ActionInputRef<TVariables, TData, TError, TSnapshot>;
  refs: ActionRuntimeRefs<TData, TError, TSnapshot>;
  cleanupAttempt: (attemptId: number) => void;
}

interface InvocationInput<TVariables, TData, TError, TSnapshot> {
  vars: TVariables;
  handlers: InvocationHandlers<TData, TError> | undefined;
  inputRef: ActionInputRef<TVariables, TData, TError, TSnapshot>;
  refs: ActionRuntimeRefs<TData, TError, TSnapshot>;
  mutation: ActionMutation<TVariables, TData, TError>;
  cleanupAttempt: (attemptId: number) => void;
}

const ATTEMPT_ID_INITIAL = 0;

export function useActionHook<TVariables, TData, TError = unknown, TSnapshot = unknown>(
  input: ActionHookOptions<TVariables, TData, TError, TSnapshot>,
): ActionResult<TVariables, TData, TError> {
  const inputRef = useLatestActionInputRef(input);
  const refs = useActionRuntimeRefs<TData, TError, TSnapshot>();
  const cleanupAttempt = useCleanupAttempt(refs);
  useUnmountAttemptCleanup(refs);

  const mutation = useActionMutation({
    inputRef,
    refs,
    cleanupAttempt,
  });
  const mutationRef = useLatestMutationRef(mutation);
  const invoke = useActionInvoke({
    inputRef,
    refs,
    mutationRef,
    cleanupAttempt,
  });
  const reset = useActionReset({ refs, mutationRef });

  const status = mapMutationStatus(mutation);
  const data = mutation.data == null ? null : mutation.data;
  const error = mutation.error == null ? null : mutation.error;

  // The bag's identity moves only when a member does. Consumers list this
  // object — or callbacks closing over it — in dependency arrays to react to
  // status/data transitions; a fresh literal every render re-runs all of them
  // and silently defeats every memo above the call site.
  return useMemo(
    () => ({ invoke, status, data, error, reset }),
    [invoke, status, data, error, reset],
  );
}

function useLatestActionInputRef<TVariables, TData, TError, TSnapshot>(
  input: ActionHookOptions<TVariables, TData, TError, TSnapshot>,
): ActionInputRef<TVariables, TData, TError, TSnapshot> {
  const inputRef = useRef(input);
  useEffect(() => {
    inputRef.current = input;
  });
  return inputRef;
}

// React Query rebuilds the mutation result object every render, while invoke
// and reset must stay referentially stable AND read live pending state at call
// time. Capturing the result would force a choice between churning callbacks
// and a stale isPending; the latest-ref gives stable callbacks a live view —
// the same idiom the options input uses above.
function useLatestMutationRef<TVariables, TData, TError>(
  mutation: ActionMutation<TVariables, TData, TError>,
): ActionMutationRef<TVariables, TData, TError> {
  const mutationRef = useRef(mutation);
  useEffect(() => {
    mutationRef.current = mutation;
  });
  return mutationRef;
}

function useActionRuntimeRefs<TData, TError, TSnapshot>(): ActionRuntimeRefs<
  TData,
  TError,
  TSnapshot
> {
  const attemptIdRef = useRef(ATTEMPT_ID_INITIAL);
  const handlersRef = useRef<Map<number, InvocationHandlers<TData, TError>>>(
    new Map(),
  );
  const snapshotsRef = useRef<Map<number, TSnapshot>>(new Map());
  const abortersRef = useRef<Map<number, AbortController>>(new Map());
  const mountedRef = useRef(true);

  return useMemo(
    () => ({
      attemptIdRef,
      handlersRef,
      snapshotsRef,
      abortersRef,
      mountedRef,
    }),
    [abortersRef, attemptIdRef, handlersRef, mountedRef, snapshotsRef],
  );
}

function useCleanupAttempt<TData, TError, TSnapshot>(
  refs: ActionRuntimeRefs<TData, TError, TSnapshot>,
): (attemptId: number) => void {
  return useCallback(
    (attemptId: number): void => {
      cleanupActionAttempt({ refs, attemptId });
    },
    [refs],
  );
}

function useUnmountAttemptCleanup<TData, TError, TSnapshot>({
  abortersRef,
  handlersRef,
  mountedRef,
  snapshotsRef,
}: ActionRuntimeRefs<TData, TError, TSnapshot>): void {
  useEffect(() => {
    mountedRef.current = true;
    const aborters = abortersRef;
    const handlers = handlersRef;
    const snapshots = snapshotsRef;
    return () => {
      mountedRef.current = false;
      abortActionControllers(aborters);
      handlers.current.clear();
      snapshots.current.clear();
    };
  }, [abortersRef, handlersRef, mountedRef, snapshotsRef]);
}

function useActionMutation<TVariables, TData, TError, TSnapshot>({
  inputRef,
  refs,
  cleanupAttempt,
}: ActionMutationInput<TVariables, TData, TError, TSnapshot>): ActionMutation<
  TVariables,
  TData,
  TError
> {
  return useMutation<TData, TError, MutateArgs<TVariables>>({
    mutationFn: ({ vars, attemptId }) =>
      runMutationFunction({ inputRef, refs, vars, attemptId }),
    onSuccess: (data, { vars, attemptId }) => {
      handleActionSuccess({
        data,
        vars,
        attemptId,
        inputRef,
        refs,
        cleanupAttempt,
      });
    },
    onError: (error, { vars, attemptId }) => {
      handleActionError({
        error,
        vars,
        attemptId,
        inputRef,
        refs,
        cleanupAttempt,
      });
    },
  });
}

function useActionInvoke<TVariables, TData, TError, TSnapshot>({
  inputRef,
  refs,
  mutationRef,
  cleanupAttempt,
}: Omit<
  InvocationInput<TVariables, TData, TError, TSnapshot>,
  "vars" | "handlers" | "mutation"
> & {
  mutationRef: ActionMutationRef<TVariables, TData, TError>;
}): ActionResult<TVariables, TData, TError>["invoke"] {
  return useCallback(
    (vars: TVariables, handlers?: InvocationHandlers<TData, TError>): void => {
      invokeAction({
        vars,
        handlers,
        inputRef,
        refs,
        mutation: mutationRef.current,
        cleanupAttempt,
      });
    },
    [cleanupAttempt, inputRef, mutationRef, refs],
  );
}

function useActionReset<TVariables, TData, TError, TSnapshot>(input: {
  refs: ActionRuntimeRefs<TData, TError, TSnapshot>;
  mutationRef: ActionMutationRef<TVariables, TData, TError>;
}): () => void {
  const { refs, mutationRef } = input;
  return useCallback((): void => {
    resetActionState({ refs, mutation: mutationRef.current });
  }, [mutationRef, refs]);
}

function cleanupActionAttempt<TData, TError, TSnapshot>({
  refs,
  attemptId,
}: CleanupAttemptInput<TData, TError, TSnapshot>): void {
  refs.handlersRef.current.delete(attemptId);
  refs.snapshotsRef.current.delete(attemptId);
  refs.abortersRef.current.delete(attemptId);
}

function runMutationFunction<TVariables, TData, TError, TSnapshot>(input: {
  inputRef: ActionInputRef<TVariables, TData, TError, TSnapshot>;
  refs: ActionRuntimeRefs<TData, TError, TSnapshot>;
  vars: TVariables;
  attemptId: number;
}): Promise<TData> {
  const { mutationFn, abortable } = input.inputRef.current;
  if (!abortable) return mutationFn(input.vars);

  const ctrl = new AbortController();
  input.refs.abortersRef.current.set(input.attemptId, ctrl);
  return mutationFn(input.vars, { signal: ctrl.signal });
}

function handleActionSuccess<TVariables, TData, TError, TSnapshot>({
  data,
  vars,
  attemptId,
  inputRef,
  refs,
  cleanupAttempt,
}: SuccessSettlementInput<TVariables, TData, TError, TSnapshot>): void {
  const handlers = selectActiveAttemptHandlers({ refs, attemptId, cleanupAttempt });
  if (!handlers) return;

  try {
    inputRef.current.onInvalidate?.(data, vars);
    handlers.onSuccess?.(data);
  } finally {
    settleActionAttempt({ handlers, attemptId, cleanupAttempt });
  }
}

function handleActionError<TVariables, TData, TError, TSnapshot>({
  error,
  vars,
  attemptId,
  inputRef,
  refs,
  cleanupAttempt,
}: ErrorSettlementInput<TVariables, TData, TError, TSnapshot>): void {
  const handlers = selectActiveAttemptHandlers({ refs, attemptId, cleanupAttempt });
  if (!handlers) return;

  try {
    rollbackOptimisticAttempt({ inputRef, refs, vars, attemptId });
    handlers.onError?.(error);
  } finally {
    settleActionAttempt({ handlers, attemptId, cleanupAttempt });
  }
}

function selectActiveAttemptHandlers<TData, TError, TSnapshot>(input: {
  refs: ActionRuntimeRefs<TData, TError, TSnapshot>;
  attemptId: number;
  cleanupAttempt: (attemptId: number) => void;
}): InvocationHandlers<TData, TError> | null {
  if (!input.refs.mountedRef.current) {
    input.cleanupAttempt(input.attemptId);
    return null;
  }

  const handlers = input.refs.handlersRef.current.get(input.attemptId);
  if (!handlers) {
    input.cleanupAttempt(input.attemptId);
    return null;
  }

  return handlers;
}

function settleActionAttempt<TData, TError>(input: {
  handlers: InvocationHandlers<TData, TError>;
  attemptId: number;
  cleanupAttempt: (attemptId: number) => void;
}): void {
  input.handlers.onSettled?.();
  input.cleanupAttempt(input.attemptId);
}

function rollbackOptimisticAttempt<TVariables, TData, TError, TSnapshot>(input: {
  inputRef: ActionInputRef<TVariables, TData, TError, TSnapshot>;
  refs: ActionRuntimeRefs<TData, TError, TSnapshot>;
  vars: TVariables;
  attemptId: number;
}): void {
  const optimistic = input.inputRef.current.optimistic;
  if (!optimistic || !input.refs.snapshotsRef.current.has(input.attemptId)) {
    return;
  }

  const snapshot = input.refs.snapshotsRef.current.get(input.attemptId) as TSnapshot;
  optimistic.rollback(snapshot, input.vars);
}

function invokeAction<TVariables, TData, TError, TSnapshot>({
  vars,
  handlers,
  inputRef,
  refs,
  mutation,
  cleanupAttempt,
}: InvocationInput<TVariables, TData, TError, TSnapshot>): void {
  if (!prepareActionInvocation({ inputRef, refs, mutation, cleanupAttempt })) return;

  const attemptId = openActionAttempt({ refs, handlers });
  notifyLoadingStart({ handlers });
  applyOptimisticSnapshot({
    optimistic: inputRef.current.optimistic,
    refs,
    vars,
    attemptId,
  });
  mutation.mutate({ vars, attemptId });
}

function prepareActionInvocation<TVariables, TData, TError, TSnapshot>(input: {
  inputRef: ActionInputRef<TVariables, TData, TError, TSnapshot>;
  refs: ActionRuntimeRefs<TData, TError, TSnapshot>;
  mutation: ActionMutation<TVariables, TData, TError>;
  cleanupAttempt: (attemptId: number) => void;
}): boolean {
  const { kind = ACTION_KIND.Mutation, dedupeMode } = input.inputRef.current;
  const effectiveDedupeMode = dedupeMode ?? defaultDedupeModeForKind(kind);
  if (shouldIgnoreInvocation({ mutation: input.mutation, effectiveDedupeMode })) {
    return false;
  }

  restartPendingInvocationIfNeeded(input);
  return true;
}

function shouldIgnoreInvocation<TVariables, TData, TError>(input: {
  mutation: ActionMutation<TVariables, TData, TError>;
  effectiveDedupeMode: DedupeMode;
}): boolean {
  return input.mutation.isPending && input.effectiveDedupeMode === DEDUPE_MODE.NoOp;
}

function restartPendingInvocationIfNeeded<TVariables, TData, TError, TSnapshot>(input: {
  refs: ActionRuntimeRefs<TData, TError, TSnapshot>;
  mutation: ActionMutation<TVariables, TData, TError>;
  cleanupAttempt: (attemptId: number) => void;
}): void {
  if (!input.mutation.isPending) return;

  const previousAttemptId = input.refs.attemptIdRef.current;
  input.refs.abortersRef.current.get(previousAttemptId)?.abort();
  input.cleanupAttempt(previousAttemptId);
  input.mutation.reset();
}

function openActionAttempt<TData, TError, TSnapshot>(input: {
  refs: ActionRuntimeRefs<TData, TError, TSnapshot>;
  handlers: InvocationHandlers<TData, TError> | undefined;
}): number {
  const attemptId = input.refs.attemptIdRef.current + 1;
  input.refs.attemptIdRef.current = attemptId;
  input.refs.handlersRef.current.set(attemptId, input.handlers ?? {});
  return attemptId;
}

function notifyLoadingStart<TData, TError>(input: {
  handlers: InvocationHandlers<TData, TError> | undefined;
}): void {
  input.handlers?.onLoadingStart?.();
}

function applyOptimisticSnapshot<TVariables, TData, TError, TSnapshot>(input: {
  optimistic: OptimisticUpdateOptions<TVariables, TSnapshot> | undefined;
  refs: ActionRuntimeRefs<TData, TError, TSnapshot>;
  vars: TVariables;
  attemptId: number;
}): void {
  if (!input.optimistic) return;

  const snapshot = input.optimistic.apply(input.vars);
  input.refs.snapshotsRef.current.set(input.attemptId, snapshot);
}

function resetActionState<TVariables, TData, TError, TSnapshot>(input: {
  refs: ActionRuntimeRefs<TData, TError, TSnapshot>;
  mutation: ActionMutation<TVariables, TData, TError>;
}): void {
  abortActionControllers(input.refs.abortersRef);
  input.refs.handlersRef.current.clear();
  input.refs.snapshotsRef.current.clear();
  input.refs.attemptIdRef.current += 1;
  input.mutation.reset();
}

function abortActionControllers(
  abortersRef: MutableRefObject<Map<number, AbortController>>,
): void {
  abortersRef.current.forEach((ctrl) => ctrl.abort());
  abortersRef.current.clear();
}

function mapMutationStatus(m: {
  isPending: boolean;
  isError: boolean;
  isSuccess: boolean;
}): InvocationStatus {
  if (m.isPending) return INVOCATION_STATUS.Loading;
  if (m.isError) return INVOCATION_STATUS.Errored;
  if (m.isSuccess) return INVOCATION_STATUS.Success;
  return INVOCATION_STATUS.NotInvoked;
}

export type { InvocationHandlers };
